import { calculateGstBreakdown } from "../../../../utilis/gstCalculation";

export const DEFAULT_COMMISSION_GST_PERCENT = 10;

export const COMMISSION_TYPE_OPTIONS = [
  { value: "percentage", label: "Percentage" },
  { value: "fixed", label: "Fixed amount" },
];

export const COMMISSION_GST_OPTIONS = [
  { value: "inclusive", label: "Inclusive" },
  { value: "exclusive", label: "Exclusive" },
  { value: "none", label: "No Tax" },
];

export const LOCATION_OPTIONS = [
  { value: "onshore", label: "Onshore" },
  { value: "offshore", label: "Offshore" },
];

function toNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function roundMoney(value) {
  return Math.round(Number(value) * 100) / 100;
}

/**
 * Base commission before GST.
 * Percentage: commissionable tuition × rate / 100 (falls back to tuitionFee).
 * Fixed: rate is the commission amount.
 */
export function calculateBaseCommission({
  tuitionFee,
  commissionableTuitionFee,
  commissionType = "percentage",
  commissionRate,
}) {
  const commissionable = toNumber(commissionableTuitionFee);
  const tuition = toNumber(tuitionFee);
  // Commission % is based on commissionable tuition when available.
  const tuitionBase = commissionable > 0 ? commissionable : tuition;
  const rate = toNumber(commissionRate);

  if (commissionType === "fixed") {
    return roundMoney(Math.max(0, rate));
  }

  if (tuitionBase <= 0 || rate <= 0) return 0;
  return roundMoney((tuitionBase * rate) / 100);
}

/**
 * Expected commission with GST using the same inclusive/exclusive
 * formulas as education / migration application cost setup.
 * Prefer commissionableTuitionFee when provided.
 */
export function calculateExpectedCommission({
  tuitionFee,
  commissionableTuitionFee,
  commissionType = "percentage",
  commissionRate,
  commissionGstType = "exclusive",
  gstPercentage = DEFAULT_COMMISSION_GST_PERCENT,
}) {
  const baseCommission = calculateBaseCommission({
    tuitionFee,
    commissionableTuitionFee,
    commissionType,
    commissionRate,
  });

  const taxType =
    commissionGstType === "inclusive" || commissionGstType === "exclusive"
      ? commissionGstType
      : "notax";

  const breakdown = calculateGstBreakdown({
    baseCost: baseCommission,
    gstPercentage: taxType === "notax" ? 0 : gstPercentage,
    taxType,
  });

  const expectedCommission =
    taxType === "exclusive"
      ? roundMoney(breakdown.professional_cost)
      : roundMoney(breakdown.base_cost);

  return {
    baseCommission,
    gstAmount: roundMoney(breakdown.gst_amount || 0),
    expectedCommission,
    gstPercentage: taxType === "notax" ? 0 : toNumber(gstPercentage),
    commissionGstType: commissionGstType || "none",
    formulaLabel:
      taxType === "inclusive"
        ? "GST = Amount × Rate / (100 + Rate)"
        : taxType === "exclusive"
          ? "GST = Amount × Rate / 100"
          : "No GST applied",
  };
}

export function isFixedCommissionType(commissionType) {
  return String(commissionType || "").toLowerCase() === "fixed";
}

/**
 * Installment commission for one tuition slice.
 * Percentage uses the existing rate formula on that slice only.
 * Fixed splits the one plan amount by this slice's share of plan tuition,
 * then applies the same inclusive / exclusive GST rules.
 */
export function resolveInstallmentCommissionAmount({
  tuitionFee,
  planTuitionFee,
  commissionType = "percentage",
  commissionRate,
  commissionGstType = "exclusive",
  gstPercentage = DEFAULT_COMMISSION_GST_PERCENT,
}) {
  if (!isFixedCommissionType(commissionType)) {
    return calculateExpectedCommission({
      tuitionFee,
      commissionType: commissionType || "percentage",
      commissionRate,
      commissionGstType,
      gstPercentage,
    }).expectedCommission;
  }

  const total = calculateExpectedCommission({
    commissionType: "fixed",
    commissionRate,
    commissionGstType,
    gstPercentage,
  }).expectedCommission;

  const planTuition = toNumber(planTuitionFee);
  const slice = Math.max(0, toNumber(tuitionFee));
  if (planTuition <= 0) return slice > 0 ? total : 0;
  return roundMoney((total * slice) / planTuition);
}

/**
 * Commission totals for display: ex-GST base, exclusive (+GST on top),
 * and inclusive (same base treated as GST-inclusive total).
 * Works for saved plans without extra DB columns.
 */
export function calculateCommissionGstViews({
  tuitionFee,
  commissionableTuitionFee,
  commissionType = "percentage",
  commissionRate,
  commissionGstType = "exclusive",
  gstPercentage = DEFAULT_COMMISSION_GST_PERCENT,
}) {
  const baseExGst = calculateBaseCommission({
    tuitionFee,
    commissionableTuitionFee,
    commissionType,
    commissionRate,
  });

  const selected = calculateExpectedCommission({
    tuitionFee,
    commissionableTuitionFee,
    commissionType,
    commissionRate,
    commissionGstType,
    gstPercentage,
  });

  const taxType =
    commissionGstType === "inclusive" || commissionGstType === "exclusive"
      ? commissionGstType
      : "notax";

  if (taxType === "notax" || baseExGst <= 0) {
    return {
      baseExGst,
      exclusiveTotal: baseExGst,
      exclusiveGst: 0,
      inclusiveTotal: baseExGst,
      inclusiveGst: 0,
      selectedTotal: selected.expectedCommission,
      selectedGst: 0,
      selectedGstType: taxType,
      gstPercentage: 0,
    };
  }

  const exclusiveBreakdown = calculateGstBreakdown({
    baseCost: baseExGst,
    gstPercentage,
    taxType: "exclusive",
  });

  const inclusiveBreakdown = calculateGstBreakdown({
    baseCost: baseExGst,
    gstPercentage,
    taxType: "inclusive",
  });

  return {
    baseExGst,
    exclusiveTotal: roundMoney(exclusiveBreakdown.professional_cost),
    exclusiveGst: roundMoney(exclusiveBreakdown.gst_amount),
    inclusiveTotal: roundMoney(inclusiveBreakdown.base_cost),
    inclusiveGst: roundMoney(inclusiveBreakdown.gst_amount),
    selectedTotal: selected.expectedCommission,
    selectedGst: selected.gstAmount,
    selectedGstType: taxType,
    gstPercentage: toNumber(gstPercentage),
  };
}

export function resolveRateForLocation({
  location,
  onshoreRate,
  offshoreRate,
}) {
  if (location === "offshore") return toNumber(offshoreRate);
  return toNumber(onshoreRate);
}

/** Normalize GET /new-commissions plan row into UI shape */
export function normalizeSavedCommissionPlan(raw, fallback = {}) {
  if (!raw || typeof raw !== "object") return null;
  return {
    id: raw.id ?? null,
    applicationId: raw.application_id ?? fallback.applicationId ?? null,
    applicationCourseId:
      raw.application_course_id ?? fallback.applicationCourseId ?? null,
    clientId: raw.client_id ?? fallback.clientId ?? null,
    providerId: raw.provider_id ?? fallback.providerId ?? null,
    courseId: raw.course_id ?? fallback.courseId ?? null,
    levelId: raw.level_id ?? fallback.levelId ?? null,
    location: raw.location || fallback.location || "",
    tuitionFee: toNumber(raw.tuition_fee ?? fallback.tuitionFee),
    commissionableTuitionFee: (() => {
      const fromPlan = toNumber(
        raw.commissionable_tuition_fee ?? raw.commissionableTuitionFee,
      );
      if (fromPlan > 0) return fromPlan;
      const fromFallback = toNumber(fallback.commissionableTuitionFee);
      if (fromFallback > 0) return fromFallback;
      return fromPlan;
    })(),
    commissionType: raw.commission_type || "percentage",
    commissionRate: toNumber(raw.commission_rate),
    commissionGstType: raw.commission_gst_type || "none",
    expectedCommission: toNumber(raw.expected_commission),
    expectedCommissionExGst: toNumber(
      raw.expected_commission_ex_gst ?? raw.expectedCommissionExGst,
    ),
    expectedCommissionExclusive: toNumber(
      raw.expected_commission_exclusive ?? raw.expectedCommissionExclusive,
    ),
    expectedCommissionInclusive: toNumber(
      raw.expected_commission_inclusive ?? raw.expectedCommissionInclusive,
    ),
    currency: raw.currency || fallback.currency || "AUD",
    status: raw.status || "active",
    hasBonus: Boolean(
      raw.has_bonus ??
        raw.hasBonus ??
        fallback.hasBonus ??
        (Array.isArray(raw.bonuses) && raw.bonuses.length > 0),
    ),
    bonusFromCommissionable: Boolean(
      raw.bonus_from_commissionable ??
        raw.bonusFromCommissionable ??
        fallback.bonusFromCommissionable,
    ),
    studentDiscountType:
      raw.student_discount_type ?? raw.studentDiscountType ?? null,
    studentDiscountPercent: toNumber(
      raw.student_discount_percent ?? raw.studentDiscountPercent,
    ),
    studentDiscountAmount: toNumber(
      raw.student_discount_amount ?? raw.studentDiscountAmount,
    ),
    remarks: raw.remarks || "",
    note: raw.note || raw.remarks || "",
    createdAt: raw.created_at || null,
    updatedAt: raw.updated_at || null,
  };
}

export function normalizeSavedCommissionBonus(raw) {
  if (!raw || typeof raw !== "object") return null;

  const bonusType = String(raw.bonus_type ?? raw.bonusType ?? "fixed").toLowerCase();
  let bonusValue = toNumber(raw.bonus_value ?? raw.bonusValue);
  const expectedBonus = toNumber(raw.expected_bonus ?? raw.expectedBonus);
  if (bonusValue <= 0 && bonusType === "fixed" && expectedBonus > 0) {
    bonusValue = expectedBonus;
  }

  return {
    id: raw.id ?? null,
    commissionPlanId: raw.commission_plan_id ?? raw.commissionPlanId ?? null,
    bonusName: raw.bonus_name ?? raw.bonusName ?? "",
    bonusType,
    bonusValue,
    gstType: raw.gst_type ?? raw.gstType ?? "none",
    expectedBonus,
    status: raw.status || "active",
    remarks: raw.remarks ?? "",
    createdAt: raw.created_at ?? raw.createdAt ?? null,
    updatedAt: raw.updated_at ?? raw.updatedAt ?? null,
    ...(Array.isArray(raw.installments)
      ? { installments: raw.installments }
      : {}),
  };
}

/** Unified bonus row for forms and display (camel + snake_case). */
export function readBonusRecord(bonus) {
  const normalized = normalizeSavedCommissionBonus(bonus);
  if (!normalized) return null;
  return normalized;
}

export function extractBonusesFromResponse(data) {
  if (!data || typeof data !== "object") return [];

  const list = Array.isArray(data.bonuses)
    ? data.bonuses
    : Array.isArray(data.new_commission_bonuses)
      ? data.new_commission_bonuses
      : Array.isArray(data.commission_bonuses)
        ? data.commission_bonuses
        : Array.isArray(data?.new_commission_plan?.bonuses)
          ? data.new_commission_plan.bonuses
          : Array.isArray(data?.commission_plan?.bonuses)
            ? data.commission_plan.bonuses
            : [];

  return list.map((row) => normalizeSavedCommissionBonus(row)).filter(Boolean);
}

/**
 * Expected bonus with same GST formulas as commission / application.
 * percentage → commissionable tuition × value / 100; fixed → value is the amount.
 */
export function calculateExpectedBonus({
  tuitionFee,
  commissionableTuitionFee,
  bonusType = "fixed",
  bonusValue,
  gstType = "exclusive",
  gstPercentage = DEFAULT_COMMISSION_GST_PERCENT,
}) {
  const result = calculateExpectedCommission({
    tuitionFee,
    commissionableTuitionFee,
    commissionType: bonusType === "percentage" ? "percentage" : "fixed",
    commissionRate: bonusValue,
    commissionGstType: gstType,
    gstPercentage,
  });

  return {
    baseBonus: result.baseCommission,
    gstAmount: result.gstAmount,
    expectedBonus: result.expectedCommission,
    gstPercentage: result.gstPercentage,
    gstType: gstType || "none",
    formulaLabel: result.formulaLabel,
  };
}

export function isBonusFromCommissionableEnabled(plan = {}) {
  return Boolean(
    plan?.bonusFromCommissionable ?? plan?.bonus_from_commissionable,
  );
}

/** Ex-GST bonus amount to subtract from commissionable tuition when enabled. */
export function resolveBonusDeductionExGst({
  plan = {},
  bonus = null,
  tuitionFee = 0,
  commissionableTuitionFee = 0,
} = {}) {
  if (!isBonusFromCommissionableEnabled(plan) || !bonus) return 0;

  const bonusType = bonus.bonusType || bonus.bonus_type || "fixed";
  const bonusValue = toNumber(bonus.bonusValue ?? bonus.bonus_value);
  const gstType = bonus.gstType || bonus.gst_type || "none";

  if (bonusValue <= 0 && !bonus.id) return 0;

  const preview = calculateExpectedBonus({
    tuitionFee,
    commissionableTuitionFee,
    bonusType,
    bonusValue,
    gstType,
  });

  return roundMoney(Math.max(0, preview.baseBonus));
}

export function resolveEffectiveCommissionableTuitionFee({
  plan = {},
  bonus = null,
  tuitionFee = 0,
  commissionableTuitionFee = 0,
} = {}) {
  const gross = toNumber(commissionableTuitionFee);
  if (!isBonusFromCommissionableEnabled(plan)) return roundMoney(gross);

  const deduction = resolveBonusDeductionExGst({
    plan,
    bonus,
    tuitionFee,
    commissionableTuitionFee: gross,
  });

  return roundMoney(Math.max(0, gross - deduction));
}

/** Tuition installment cap (commissionable, or commissionable − bonus when enabled). */
export function resolvePlanTuitionInstallmentBudget(plan = {}, bonus = null) {
  const gross =
    toNumber(plan?.commissionableTuitionFee ?? plan?.commissionable_tuition_fee) ||
    toNumber(plan?.tuitionFee ?? plan?.tuition_fee);
  return resolveEffectiveCommissionableTuitionFee({
    plan,
    bonus,
    tuitionFee: plan?.tuitionFee ?? plan?.tuition_fee,
    commissionableTuitionFee: gross,
  });
}

export function buildNewCommissionBonusPayload({
  bonusName,
  bonusType,
  bonusValue,
  gstType,
  expectedBonus,
  status = "active",
  remarks = "",
}) {
  return {
    bonus_name: bonusName || null,
    bonus_type: bonusType || "fixed",
    bonus_value: toNumber(bonusValue),
    gst_type: gstType || "none",
    expected_bonus: toNumber(expectedBonus),
    status: status || "active",
    remarks: remarks || null,
  };
}

export function commissionGstTypeToInvoiceTaxType(gstType) {
  const normalized = String(gstType || "")
    .trim()
    .toLowerCase();
  if (normalized === "inclusive" || normalized === "exclusive") {
    return normalized;
  }
  return "notax";
}

/** Plan fields used by commission create/edit invoice wizard. */
export function buildCommissionInvoicePlanMeta(plan) {
  const normalized =
    plan && typeof plan === "object" && plan.commissionGstType != null
      ? plan
      : normalizeSavedCommissionPlan(plan);
  if (!normalized) return {};

  const gstType = normalized.commissionGstType || "none";

  return {
    student_discount_type: normalized.studentDiscountType,
    student_discount_percent: normalized.studentDiscountPercent,
    student_discount_amount: normalized.studentDiscountAmount,
    commissionable_tuition_fee: normalized.commissionableTuitionFee,
    commission_gst_type: gstType,
    defaultTaxType: commissionGstTypeToInvoiceTaxType(gstType),
    defaultGstPercentage: DEFAULT_COMMISSION_GST_PERCENT,
    studentDiscountType: normalized.studentDiscountType,
    studentDiscountPercent: normalized.studentDiscountPercent,
    studentDiscountAmount: normalized.studentDiscountAmount,
    commissionableTuitionFee: normalized.commissionableTuitionFee,
    commissionGstType: gstType,
  };
}

export function buildStudentDiscountPlanPayload({
  discountMode = "percentage",
  discountValue = "",
  commissionableTuitionFee = 0,
} = {}) {
  const value = toNumber(discountValue);
  const mode = discountMode === "fixed" ? "fixed" : "percentage";
  const commissionable = Math.max(toNumber(commissionableTuitionFee), 0);

  if (value <= 0) {
    return {
      student_discount_type: null,
      student_discount_percent: null,
      student_discount_amount: null,
    };
  }

  if (mode === "fixed") {
    const amount = roundMoney(value);
    const percent =
      commissionable > 0 ? roundMoney((amount / commissionable) * 100) : null;
    return {
      student_discount_type: "fixed",
      student_discount_percent: percent,
      student_discount_amount: amount,
    };
  }

  const percent = roundMoney(value);
  const amount =
    commissionable > 0 ? roundMoney((commissionable * percent) / 100) : null;

  return {
    student_discount_type: "percentage",
    student_discount_percent: percent,
    student_discount_amount: amount,
  };
}

export function buildNewCommissionPlanPayload({
  applicationId,
  applicationCourseId,
  clientId,
  providerId,
  courseId,
  levelId,
  location,
  tuitionFee,
  commissionable_tuition_fee,
  commissionType,
  commissionRate,
  commissionGstType,
  expectedCommission,
  expectedCommissionExGst,
  expectedCommissionExclusive,
  expectedCommissionInclusive,
  currency = "AUD",
  status = "active",
  hasBonus = false,
  bonusFromCommissionable = false,
  remarks = "",
  note = "",
}) {
  return {
    application_id: Number(applicationId),
    application_course_id: Number(applicationCourseId),
    client_id: clientId != null && clientId !== "" ? Number(clientId) : null,
    provider_id:
      providerId != null && providerId !== "" ? Number(providerId) : null,
    course_id: courseId != null && courseId !== "" ? Number(courseId) : null,
    level_id:
      levelId != null && levelId !== "" && Number(levelId) !== 0
        ? Number(levelId)
        : null,
    location: location || null,
    tuition_fee: toNumber(tuitionFee),
    commissionable_tuition_fee: toNumber(commissionable_tuition_fee),
    commission_type: commissionType || "percentage",
    commission_rate: toNumber(commissionRate),
    commission_gst_type: commissionGstType || "none",
    expected_commission: toNumber(expectedCommission),
    expected_commission_ex_gst: toNumber(expectedCommissionExGst),
    expected_commission_exclusive: toNumber(expectedCommissionExclusive),
    expected_commission_inclusive: toNumber(expectedCommissionInclusive),
    currency: currency || "AUD",
    status: status || "active",
    has_bonus: Boolean(hasBonus),
    bonus_from_commissionable: Boolean(bonusFromCommissionable),
    remarks: remarks || null,
    note: note || null,
  };
}
